import React, { useEffect, useRef, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, KeyRound, Archive, ArchiveRestore, Trash2, Pencil, Loader2, Shield, CheckCircle2, AlertTriangle, Sparkles } from "lucide-react";
import PermissionsDialog, { loadCatalog } from "@/pages/AdminUsersPermissionsDialog";
import { computePresetDiff, presetSortKey, STATUS_STYLE } from "@/lib/presetDiff";

// -----------------------------------------------------------------------------
// Roles — "Bar (legacy)" (`bar_operator`) removed in v8.1, replaced by `bartender`.
// -----------------------------------------------------------------------------
const ROLES = [
  ["super_admin", "Super Admin"],
  ["admin", "Amministratore"],
  ["manager", "Responsabile"],
  ["bartender", "Bartender"],
  ["bar", "Bar"],
  ["sala", "Sala"],
  ["cucina", "Cucina"],
  ["spiaggia", "Spiaggia"],
  ["runner", "Runner"],
  ["analyst", "Analista"],
];

const roleLabel = (k) => (ROLES.find((r) => r[0] === k)?.[1]) || k;

export default function AdminUsers() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [presetFilter, setPresetFilter] = useState(""); // "", identical, modified, custom
  const [sortBy, setSortBy] = useState("name");
  const [pendingRoleChange, setPendingRoleChange] = useState(null);

  const load = async () => {
    const inc = statusFilter === "archived" || statusFilter === "all";
    setUsers((await api.get(`/admin/users${inc ? "?include_archived=true" : ""}`)).data);
  };
  useEffect(() => { load(); }, [statusFilter]);
  useEffect(() => { loadCatalog().then(setCatalog).catch(() => {}); }, []);

  // ---------------------------------------------------------------------------
  // Enrich each user with its live preset diff. Super Admin is skipped
  // (implicit bypass) and rendered as its own badge.
  // ---------------------------------------------------------------------------
  const presetsByRole = catalog?.role_presets || {};
  const enriched = useMemo(() => users.map((u) => {
    if (u.role === "super_admin") return { ...u, _diff: null };
    const preset = presetsByRole[u.role] || [];
    return { ...u, _diff: computePresetDiff(u.permissions || [], preset) };
  }), [users, presetsByRole]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let arr = enriched.filter((u) => {
      if (q && !`${u.name || ""} ${u.email || ""}`.toLowerCase().includes(q)) return false;
      if (roleFilter && u.role !== roleFilter) return false;
      if (statusFilter === "active" && (u.archived || !u.active)) return false;
      if (statusFilter === "inactive" && (u.archived || u.active)) return false;
      if (statusFilter === "archived" && !u.archived) return false;
      if (presetFilter && u.role !== "super_admin") {
        const s = u._diff?.status;
        if (presetFilter === "identical" && s !== "identical") return false;
        if (presetFilter === "modified"  && s !== "modified") return false;
        if (presetFilter === "custom"    && !(s === "custom" || s === "empty")) return false;
      }
      // Super Admin never matches a preset filter (bypass).
      if (presetFilter && u.role === "super_admin") return false;
      return true;
    });
    arr = arr.sort((a, b) => {
      if (sortBy === "preset") {
        // Super Admin sorted last for preset ordering.
        const av = a.role === "super_admin" ? 100 : presetSortKey(a._diff);
        const bv = b.role === "super_admin" ? 100 : presetSortKey(b._diff);
        if (av !== bv) return av - bv;
        return (a.name || "").localeCompare(b.name || "");
      }
      const av = (a[sortBy] || "") + ""; const bv = (b[sortBy] || "") + "";
      return av.localeCompare(bv);
    });
    return arr;
  }, [enriched, search, roleFilter, statusFilter, presetFilter, sortBy]);

  // Optimistic in-place update helper.
  const patchLocal = (id, patch) => {
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, ...patch } : u)));
  };

  const toggleActive = async (u) => {
    const next = !u.active;
    patchLocal(u.id, { active: next });
    try { await api.patch(`/admin/users/${u.id}?active=${next}`); toast.success(next ? "Utente attivato" : "Utente disattivato"); }
    catch (e) { patchLocal(u.id, { active: u.active }); toast.error(formatApiError(e)); }
  };

  const commitRoleChange = async (u, role, mode) => {
    const prev = { role: u.role, permissions: u.permissions };
    patchLocal(u.id, { role });
    try {
      await api.patch(`/admin/users/${u.id}?role=${role}`);
      if (mode === "preset") {
        const preset = presetsByRole[role] || [];
        await api.patch(`/admin/users/${u.id}/permissions`,
                        { custom_permissions: preset, source: "preset" });
        patchLocal(u.id, { permissions: preset });
        toast.success("Ruolo aggiornato · preset applicato");
      } else {
        toast.success("Ruolo aggiornato · permessi invariati");
      }
    } catch (e) {
      patchLocal(u.id, prev);
      toast.error(formatApiError(e));
    }
  };

  const requestRoleChange = (u, role) => {
    if (role === u.role) return;
    if (u.role === "super_admin") return;
    setPendingRoleChange({ user: u, newRole: role });
  };

  const archive = async (u) => {
    if (!window.confirm(u.archived ? `Riattivare ${u.email}?` : `Archiviare ${u.email}? Lo storico verrà preservato.`)) return;
    try { await api.post(`/admin/users/${u.id}/${u.archived ? "unarchive" : "archive"}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const deleteUser = async (u) => {
    if (!window.confirm(`ELIMINARE definitivamente ${u.email}?\n\nQuesta operazione è irreversibile e rimuove l'utente dal database. L'archivio audit resta preservato.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Utente eliminato");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const canEditSuperadmin = me?.role === "super_admin";

  return (
    <div>
      <div className="eyebrow">Team</div>
      <div className="flex justify-between items-center mt-1">
        <h1 className="text-4xl">Staff & Permessi</h1>
        <NewUserDialog onSaved={load} meIsSuper={canEditSuperadmin} />
      </div>

      <div className="card-elevated p-4 mt-5 grid gap-3 md:grid-cols-5">
        <div><Label>Cerca</Label>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nome o email" data-testid="users-search" />
        </div>
        <div><Label>Ruolo</Label>
          <Select value={roleFilter || "all"} onValueChange={(v) => setRoleFilter(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="users-role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              {ROLES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label>Stato</Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="users-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Attivi</SelectItem>
              <SelectItem value="inactive">Disattivati</SelectItem>
              <SelectItem value="archived">Archiviati</SelectItem>
              <SelectItem value="all">Tutti</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Preset</Label>
          <Select value={presetFilter || "all"} onValueChange={(v) => setPresetFilter(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="users-preset-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti</SelectItem>
              <SelectItem value="identical">Identico al preset</SelectItem>
              <SelectItem value="modified">Modificato</SelectItem>
              <SelectItem value="custom">Custom</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Ordina per</Label>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger data-testid="users-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Nome</SelectItem>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="role">Ruolo</SelectItem>
              <SelectItem value="preset">Preset</SelectItem>
              <SelectItem value="last_login_at">Ultimo accesso</SelectItem>
              <SelectItem value="created_at">Data creazione</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="card-elevated mt-4 p-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-ink-500">
            <tr>
              <th className="text-left p-2">Nome</th>
              <th className="text-left">Email</th>
              <th>Ruolo</th>
              <th>Preset</th>
              <th>Attivo</th>
              <th>Creato</th>
              <th>Ultimo accesso</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((u) => {
              const isSuperTarget = u.role === "super_admin";
              const disabled = isSuperTarget && !canEditSuperadmin;
              return (
                <tr key={u.id} className={`border-t border-sand-300 ${u.archived ? "opacity-50" : ""}`} data-testid={`user-${u.id}`}>
                  <td className="p-2">
                    <InlineName user={u} disabled={disabled} onSaved={(nm) => patchLocal(u.id, { name: nm })} />
                    {u.id === me?.id && <span className="ml-2 text-xs text-bronze-600">(tu)</span>}
                    {u.archived && <span className="ml-2 chip text-[10px] bg-amber-50 text-amber-800 border-amber-200">Archiviato</span>}
                  </td>
                  <td>{u.email}</td>
                  <td className="text-center">
                    <Select value={u.role} onValueChange={(v) => requestRoleChange(u, v)} disabled={disabled || u.id === me?.id || u.archived}>
                      <SelectTrigger className="w-40" data-testid={`role-${u.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {ROLES.filter(([k]) => k !== "super_admin" || canEditSuperadmin).map(([k, l]) =>
                          <SelectItem key={k} value={k}>{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="text-center">
                    <PresetCell user={u} />
                  </td>
                  <td className="text-center">
                    <Switch checked={u.active} onCheckedChange={() => toggleActive(u)}
                            disabled={disabled || u.id === me?.id || u.archived || isSuperTarget} data-testid={`active-${u.id}`} />
                  </td>
                  <td className="text-center text-ink-500 text-xs">{u.created_at?.slice(0, 10) || "-"}</td>
                  <td className="text-center text-ink-500 text-xs">{u.last_login_at?.slice(0, 16) || "-"}</td>
                  <td className="text-center">
                    <div className="inline-flex gap-1">
                      <SetPinDialog user={u} disabled={disabled || u.archived} onSaved={load} />
                      <PermissionsDialog user={u} disabled={disabled || u.archived}
                                          rolePreset={presetsByRole[u.role] || []}
                                          onSaved={(perms) => perms && patchLocal(u.id, { permissions: perms })} />
                      <ResetPasswordDialog user={u} disabled={disabled || u.archived} />
                      <Button variant="outline" size="sm" className="rounded-full"
                              disabled={disabled || u.id === me?.id || isSuperTarget}
                              onClick={() => archive(u)} data-testid={`archive-${u.id}`}>
                        {u.archived ? <><ArchiveRestore className="w-3 h-3 mr-1" />Riattiva</>
                                    : <><Archive className="w-3 h-3 mr-1" />Archivia</>}
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-full border-rose-300 text-rose-700 hover:bg-rose-50"
                              disabled={disabled || u.id === me?.id || isSuperTarget}
                              onClick={() => deleteUser(u)} data-testid={`delete-user-${u.id}`}
                              title="Elimina definitivamente">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan="8" className="p-8 text-center text-ink-500">Nessun utente trovato.</td></tr>}
          </tbody>
        </table>
      </div>

      <RoleChangeConfirmDialog pending={pendingRoleChange}
                                onClose={() => setPendingRoleChange(null)}
                                onConfirm={async (mode) => {
                                  const p = pendingRoleChange;
                                  setPendingRoleChange(null);
                                  await commitRoleChange(p.user, p.newRole, mode);
                                }} />
    </div>
  );
}

// -----------------------------------------------------------------------------
// PresetCell — status badge + native tooltip. Super Admin gets a bespoke chip.
// -----------------------------------------------------------------------------
function PresetCell({ user }) {
  if (user.role === "super_admin") {
    return (
      <span className="inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5 bg-amber-50 text-amber-900 border border-amber-300"
             data-testid={`preset-cell-${user.id}`}
             title="Super Admin — permessi bypass (tutti impliciti)">
        <Shield className="w-3 h-3" /> Bypass
      </span>
    );
  }
  const d = user._diff;
  if (!d) return <span className="text-ink-400 text-xs" data-testid={`preset-cell-${user.id}`}>—</span>;
  const style = STATUS_STYLE[d.status];
  const sign = d.added.length && d.removed.length
    ? `+${d.added.length} / -${d.removed.length}`
    : d.added.length ? `+${d.added.length}`
    : d.removed.length ? `-${d.removed.length}` : "";
  const label = d.status === "identical"
    ? `✔ ${roleLabel(user.role)}`
    : d.status === "modified"
      ? `${roleLabel(user.role)} (${sign})`
      : "Custom";
  const tip = d.status === "identical"
    ? `Ruolo: ${roleLabel(user.role)}
Preset originale: ${d.presetSize} permessi
Permessi attuali: ${d.currentSize} permessi
Nessuna personalizzazione.`
    : d.status === "modified"
      ? `Ruolo: ${roleLabel(user.role)}
Preset originale: ${d.presetSize} permessi
Permessi attuali: ${d.currentSize} permessi
+${d.added.length} aggiunti
-${d.removed.length} rimossi`
      : `Ruolo: ${roleLabel(user.role)}
Preset completamente personalizzato.
Preset originale: ${d.presetSize} · Permessi attuali: ${d.currentSize}
+${d.added.length} aggiunti / -${d.removed.length} rimossi`;
  const Icon = d.status === "identical" ? CheckCircle2
              : d.status === "modified" ? Sparkles
              : AlertTriangle;
  return (
    <span className={`inline-flex items-center gap-1 text-xs rounded-full px-2 py-0.5 border ${style.chip}`}
           title={tip}
           data-testid={`preset-cell-${user.id}`}
           data-preset-status={d.status}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

// -----------------------------------------------------------------------------
// InlineName — click to edit, Enter/blur saves, Esc cancels. (v8.1)
// -----------------------------------------------------------------------------
function InlineName({ user, disabled, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(user.name || "");
  const [busy, setBusy] = useState(false);
  const originalRef = useRef(user.name || "");
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing) {
      originalRef.current = user.name || "";
      setVal(user.name || "");
      setTimeout(() => inputRef.current?.select(), 0);
    }
  }, [editing, user.name]);

  const cancel = () => { setVal(originalRef.current); setEditing(false); };

  const save = async () => {
    const next = (val || "").trim();
    if (busy) return;
    if (!next || next === originalRef.current) { setEditing(false); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.id}?name=${encodeURIComponent(next)}`);
      onSaved && onSaved(next);
      toast.success("Nome aggiornato");
      setEditing(false);
    } catch (e) {
      toast.error(formatApiError(e));
      setVal(originalRef.current);
    } finally { setBusy(false); }
  };

  if (!editing) {
    return (
      <button type="button" disabled={disabled}
              onClick={() => !disabled && setEditing(true)}
              className={`group inline-flex items-center gap-1 ${disabled ? "cursor-default" : "hover:text-bronze-700"}`}
              data-testid={`name-view-${user.id}`}
              title={disabled ? "" : "Clicca per modificare il nome"}>
        <span>{user.name || <em className="text-ink-400">senza nome</em>}</span>
        {!disabled && <Pencil className="w-3 h-3 opacity-0 group-hover:opacity-60 transition-opacity" />}
      </button>
    );
  }
  return (
    <span className="inline-flex items-center gap-2">
      <Input ref={inputRef}
             value={val}
             onChange={(e) => setVal(e.target.value)}
             onKeyDown={(e) => {
               if (e.key === "Enter") { e.preventDefault(); save(); }
               if (e.key === "Escape") { e.preventDefault(); cancel(); }
             }}
             onBlur={save}
             disabled={busy}
             className="h-8 py-1 w-52"
             data-testid={`name-input-${user.id}`} />
      {busy && <Loader2 className="w-4 h-4 animate-spin text-ink-500" data-testid={`name-spinner-${user.id}`} />}
    </span>
  );
}

// -----------------------------------------------------------------------------
// RoleChangeConfirmDialog — never auto-overwrite permissions.
// -----------------------------------------------------------------------------
function RoleChangeConfirmDialog({ pending, onClose, onConfirm }) {
  const open = !!pending;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="role-change-dialog">
        <DialogHeader>
          <DialogTitle>Cambio ruolo</DialogTitle>
          <DialogDescription>
            Il ruolo identifica solo la <b>figura aziendale</b>. I permessi
            reali restano quelli assegnati all'utente.
            <br /><br />
            Vuoi anche applicare il <b>preset del nuovo ruolo</b>? Sovrascriverà
            i permessi correnti con quelli previsti dal preset di{" "}
            <b>{pending ? roleLabel(pending.newRole) : ""}</b>.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onConfirm("keep")}
                  data-testid="role-change-keep">
            Mantieni permessi attuali
          </Button>
          <Button onClick={() => onConfirm("preset")}
                  data-testid="role-change-preset">
            Applica preset
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// NewUserDialog — role auto-applies preset; admin can tweak before saving.
// -----------------------------------------------------------------------------
function NewUserDialog({ onSaved, meIsSuper }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ email: "", password: "", name: "", role: "bartender" });
  const [perms, setPerms] = useState(new Set());
  const [permsOpen, setPermsOpen] = useState(false);
  const [presetLoaded, setPresetLoaded] = useState(false);
  const [rolePreset, setRolePreset] = useState([]);

  useEffect(() => {
    if (!open) { setPresetLoaded(false); return; }
    (async () => {
      const cat = await loadCatalog();
      const preset = cat?.role_presets?.[f.role] || [];
      setRolePreset(preset);
      setPerms(new Set(preset));
      setPresetLoaded(true);
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, f.role]);

  const save = async () => {
    try {
      const r = await api.post("/admin/users", f);
      const uid = r?.data?.id;
      if (uid && f.role !== "super_admin") {
        // Preset was auto-loaded, so any deviation is "manual" from the
        // audit's perspective. Use "preset" only when the admin never
        // touched the permission set.
        const preset = new Set(rolePreset);
        const identical = preset.size === perms.size && [...perms].every((k) => preset.has(k));
        await api.patch(`/admin/users/${uid}/permissions`,
                        { custom_permissions: Array.from(perms),
                          source: identical ? "preset" : "manual" });
      }
      setOpen(false);
      setF({ email: "", password: "", name: "", role: "bartender" });
      setPerms(new Set());
      onSaved();
      toast.success("Utente creato");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="new-user-btn" className="rounded-full"><Plus className="w-4 h-4 mr-1" />Nuovo</Button>
      </DialogTrigger>
      <DialogContent><DialogHeader><DialogTitle>Nuovo utente</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nome</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="new-user-name" /></div>
          <div><Label>Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="new-user-email" /></div>
          <div><Label>Password (min 8, lettera + numero)</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="new-user-password" /></div>
          <div><Label>Ruolo</Label>
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
              <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.filter(([k]) => k !== "super_admin" || meIsSuper).map(([k, l]) =>
                  <SelectItem key={k} value={k}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-ink-500 mt-1">
              Il ruolo è solo la figura aziendale. I permessi applicati inizialmente sono il preset del ruolo — puoi personalizzarli prima di creare l'utente.
            </p>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-sand-300 px-3 py-2">
            <div className="text-sm">
              <div className="font-medium">Permessi iniziali</div>
              <div className="text-ink-500 text-xs">
                {presetLoaded ? `${perms.size} selezionati (preset di ${roleLabel(f.role)})` : "Caricamento preset…"}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setPermsOpen(true)}
                    disabled={!presetLoaded || f.role === "super_admin"}
                    data-testid="new-user-perms-btn">
              <Shield className="w-3 h-3 mr-1" />Personalizza
            </Button>
          </div>
          <Button onClick={save} className="w-full" data-testid="new-user-save">Crea</Button>
        </div>
        <PermissionsDialog user={{ email: f.email || "nuovo utente", role: f.role, permissions: Array.from(perms) }}
                            rolePreset={rolePreset}
                            embedded
                            triggerless
                            open={permsOpen}
                            onOpenChange={setPermsOpen}
                            value={Array.from(perms)}
                            onChange={setPerms} />
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ user, disabled }) {
  const [open, setOpen] = useState(false);
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async () => {
    if (pwd !== pwd2) return toast.error("Le due password non coincidono");
    if (pwd.length < 8) return toast.error("Almeno 8 caratteri");
    setLoading(true);
    try {
      await api.post(`/admin/users/${user.id}/reset-password`, { new_password: pwd });
      toast.success("Password reimpostata");
      setOpen(false); setPwd(""); setPwd2("");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full" disabled={disabled}
                data-testid={`reset-pwd-${user.id}`}>
          <KeyRound className="w-3 h-3 mr-1" />Reset password
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Reset password di {user.email}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-ink-500">Comunica la nuova password all'utente. Verrà applicata immediatamente.</p>
          <div><Label>Nuova password</Label>
            <Input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid={`reset-pwd-new-${user.id}`} />
          </div>
          <div><Label>Conferma password</Label>
            <Input type="password" value={pwd2} onChange={(e) => setPwd2(e.target.value)} data-testid={`reset-pwd-new2-${user.id}`} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
          <Button onClick={submit} disabled={loading} data-testid={`reset-pwd-confirm-${user.id}`}>
            {loading ? "Salvo…" : "Reimposta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { roleLabel };

// -----------------------------------------------------------------------------
// SetPinDialog — CRM v7 Staff PIN management.
// -----------------------------------------------------------------------------
function SetPinDialog({ user, disabled, onSaved }) {
  const [open, setOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!/^\d{4}$/.test(pin)) return toast.error("Il PIN deve essere 4 cifre");
    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.id}/pin`, { pin });
      toast.success("PIN impostato");
      setOpen(false); setPin("");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    if (!window.confirm(`Rimuovere il PIN di ${user.email}?`)) return;
    try {
      await api.patch(`/admin/users/${user.id}/pin`, { clear: true });
      toast.success("PIN rimosso"); setOpen(false); onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full" disabled={disabled}
                data-testid={`set-pin-${user.id}`}>
          <KeyRound className="w-3 h-3 mr-1" />
          {user.has_pin ? "PIN ✓" : "PIN"}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>PIN di {user.email}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-ink-500">
            Il PIN è di 4 cifre. Deve essere unico tra gli operatori attivi. Serve solo per l'accesso rapido a <b>/staff</b>.
          </p>
          <div>
            <Label>Nuovo PIN (4 cifre)</Label>
            <Input type="password" inputMode="numeric" pattern="\d{4}" maxLength={4}
                    value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                    data-testid={`set-pin-input-${user.id}`}
                    className="tracking-widest text-center text-2xl font-serif" />
          </div>
          {user.has_pin && (
            <div className="text-xs text-ink-500">
              Un PIN è già configurato per questo utente. Cambialo digitandone uno nuovo.
            </div>
          )}
        </div>
        <DialogFooter>
          {user.has_pin && (
            <Button variant="outline" onClick={clear} data-testid={`clear-pin-${user.id}`}>
              Rimuovi PIN
            </Button>
          )}
          <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
          <Button onClick={save} disabled={busy || pin.length !== 4}
                    data-testid={`save-pin-${user.id}`}>
            {busy ? "Salvo…" : "Salva PIN"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
